import { useState, useEffect } from 'react'
import { useController, type Control, type FieldPath, type FieldValues } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  TextField,
  Input,
  Label,
  FieldError,
  Text,
} from 'react-aria-components'
import { Phone01Icon } from '@untitledui-icons/react/line'
import { cn } from '@/lib/utils'

export interface PhoneNumberInputProps<TFieldValues extends FieldValues> {
  control: Control<TFieldValues>
  name: FieldPath<TFieldValues>
  label?: string
  description?: string
  placeholder?: string
  isDisabled?: boolean
  isRequired?: boolean
  defaultCountryCode?: string
  className?: string
  autoComplete?: string
}

export function PhoneNumberInput<TFieldValues extends FieldValues>({
  control,
  name,
  label,
  description,
  placeholder = '+31 6 12345678',
  isDisabled,
  isRequired,
  defaultCountryCode = '+31',
  className,
  autoComplete,
}: PhoneNumberInputProps<TFieldValues>) {
  const { t } = useTranslation()
  const {
    field,
    fieldState: { error },
  } = useController({
    control,
    name,
  })

  // Initialize display value from field value
  const [displayValue, setDisplayValue] = useState(() => {
    return field.value ? (field.value as string) : ''
  })

  // Sync displayValue when field.value changes externally (e.g., form reset)
  useEffect(() => {
    const fieldVal = field.value ? (field.value as string) : ''
    if (fieldVal !== displayValue) {
      setDisplayValue(fieldVal)
    }
    // Only run when field.value changes, not when displayValue changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value])

  const formatPhoneNumber = (value: string): string => {
    // Remove all non-digit characters except +
    let cleaned = value.replace(/[^\d+]/g, '')

    // If doesn't start with +, assume it needs country code
    if (cleaned && !cleaned.startsWith('+')) {
      // If starts with 0, it's likely a local number - replace 0 with country code
      if (cleaned.startsWith('0')) {
        cleaned = defaultCountryCode + cleaned.substring(1)
      } else {
        cleaned = defaultCountryCode + cleaned
      }
    }

    return cleaned
  }

  const handleChange = (value: string) => {
    setDisplayValue(value)

    // Store the formatted E.164 format
    const formatted = formatPhoneNumber(value)
    field.onChange(formatted)
  }

  const handleBlur = () => {
    // On blur, format the display value
    const formatted = formatPhoneNumber(displayValue)
    setDisplayValue(formatted)
    field.onBlur()
  }

  return (
    <TextField
      className={cn('flex flex-col gap-1.5', className)}
      isDisabled={isDisabled}
      isRequired={isRequired}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
    >
      {label && (
        <Label className="text-sm font-medium text-gray-700 dark:text-gray-200">
          {label}
          {isRequired && <span className="text-error-500 ml-0.5">*</span>}
        </Label>
      )}
      <div className="relative">
        <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          <Phone01Icon className="h-5 w-5" />
        </div>
        <Input
          type="tel"
          placeholder={placeholder}
          autoComplete={autoComplete}
          className={cn(
            'w-full rounded-lg border border-gray-300 bg-white pl-10 pr-3.5 py-2.5 text-base',
            'placeholder:text-gray-400',
            'focus:border-brand-500 focus:ring-1 focus:ring-brand-500 focus:outline-none',
            'disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed',
            'dark:bg-gray-900 dark:border-gray-700 dark:text-gray-100',
            'dark:focus:border-brand-400 dark:focus:ring-brand-400',
            error && 'border-error-500 focus:border-error-500 focus:ring-error-500'
          )}
        />
      </div>
      {description && !error && (
        <Text slot="description" className="text-sm text-gray-500 dark:text-gray-400">
          {description}
        </Text>
      )}
      {error && (
        <FieldError className="text-sm text-error-600 dark:text-error-400">
          {t(error.message ?? '')}
        </FieldError>
      )}
    </TextField>
  )
}

export default PhoneNumberInput
